import {
  Card,
  Text,
  BlockStack,
  Box,
  InlineStack,
  Button,
  Divider,
  Thumbnail,
  Bleed,
} from "@medentic/polaris-react";
import { useTranslation } from "react-i18next";
import { SubscriptionData, CreditBillingStrategy, UsageRateItem, UsageRate } from "../types";
import { useState, useMemo, useEffect, useCallback } from "react";
import { AddCreditsModal } from "./AddCreditsModal";
import { ExportIcon } from "@medentic/polaris-icons";
import { CountryCombobox } from "@shared/ui";
import { useCountries } from "@shared/hooks/common/useCountries";
import { useGetSingleCompanyDetails } from "@shared/hooks/common/useGetSingleCompanyDetails";
import { getServiceIcon, getServiceName } from "../constants";
import { useParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import { showToast } from "@shared/lib";
import useApiRequest from "@shared/hooks/common/useApiRequest";

interface CreditsCardProps {
  data: SubscriptionData;
  clinicId: string;
}



export function CreditsCard({ data, clinicId }: CreditsCardProps) {
  const { t } = useTranslation();
  const { clinicId: paramClinicId } = useParams<{ clinicId: string }>();
  const dispatch = useDispatch();
  const { GET } = useApiRequest();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  
  // Use clinic ID from params if available, otherwise from props
  const currentClinicId = paramClinicId || clinicId;
  
  // State for selected countries for SMS services
  const [selectedCountries, setSelectedCountries] = useState<{ [smsType: string]: string }>({});

  // Get clinic's country information - handle loading gracefully
  const { countries, isLoading: isCountriesLoading } = useCountries();
  const { company } = useGetSingleCompanyDetails(currentClinicId, {
    enabled: Boolean(currentClinicId),
  });
  
  // Derive clinic country ISO3 code when data is available
  const clinicCountryIso3 = useMemo(() => {
    if (isCountriesLoading || !company?.address?.countryISO3) {
      return null;
    }
    const country = countries.find(c => c.iso3 === company.address.countryISO3);
    return country?.iso3 || null;
  }, [countries, isCountriesLoading, company?.address?.countryISO3]);

  const { credits } = data;

  // Handle export credits
  const handleExportCredits = async () => {
    try {
      setIsExporting(true);
      const response = await GET<{ url?: string; message?: string }>(
        `/api/healthcare-companies/${currentClinicId}/billing/credits/export`
      );

      if (response.url) {
        // Handle file download
        const link = document.createElement("a");
        link.href = response.url;
        link.setAttribute("download", "credits-export.csv");
        document.body.appendChild(link);
        link.click();
        link.remove();
        
        dispatch(
          showToast({
            message: t("settings.subscription.credits.exportSuccess", { 
              defaultValue: "Credits exported successfully" 
            }),
            type: "success",
          })
        );
      } else if (response.message) {
        // Handle message response
        dispatch(
          showToast({
            message: response.message,
            type: "success",
          })
        );
      }
    } catch (error) {
      // Error is automatically handled by useApiRequest hook
      console.error("Export failed:", error);
    } finally {
      setIsExporting(false);
    }
  };

  // Filter and validate usage rates
  const validUsageRates = useMemo(() => {
    if (!Array.isArray(credits.usageRates)) {
      console.warn('Credits usage rates is not an array:', credits.usageRates);
      return [];
    }

    return credits.usageRates.filter((rate: UsageRate) => {
      if (!rate || !rate.id) {
        console.warn('Invalid usage rate found (missing id or null):', rate);
        return false;
      }

      // Validate per-use rates
      if (rate.billingStrategy === CreditBillingStrategy.PER_USE) {
        if (!rate.perUsePrice || typeof rate.perUsePrice.price !== 'number') {
          console.warn('Invalid per-use price data for rate:', rate.id, rate.perUsePrice);
          return false;
        }
      }

      // Validate per-country rates
      if (rate.billingStrategy === CreditBillingStrategy.PER_COUNTRY) {
        if (!rate.perCountryPrices || typeof rate.perCountryPrices !== 'object') {
          console.warn('Invalid per-country prices data for rate:', rate.id, rate.perCountryPrices);
          return false;
        }
      }

      return true;
    });
  }, [credits.usageRates]);

  // Automatically set clinic's country for SMS services on initial load
  useEffect(() => {
    if (validUsageRates.length > 0 && clinicCountryIso3) {
      // Find SMS services in usage rates and auto-select clinic's country
      validUsageRates.forEach((rate: UsageRate) => {
        if (rate.billingStrategy === CreditBillingStrategy.PER_COUNTRY && rate.perCountryPrices) {
          const availableCountries = Object.keys(rate.perCountryPrices);
          // Check if clinic's country is available for this service
          if (availableCountries.includes(clinicCountryIso3)) {
            setSelectedCountries(prev => ({
              ...prev,
              [rate.id]: clinicCountryIso3,
            }));
          }
        }
      });
    }
  }, [clinicCountryIso3, validUsageRates]);

  // Prepare usage rates as individual items
  const prepareUsageRateItems = useCallback((): UsageRateItem[] => {
    const items: UsageRateItem[] = [];

    if (validUsageRates.length === 0) return items;

    validUsageRates.forEach((rate: UsageRate) => {
      if (rate.billingStrategy === CreditBillingStrategy.PER_USE && rate.perUsePrice) {
        // For Viber and WhatsApp - simple per-use pricing
        items.push({
          type: rate.id,
          name: getServiceName(rate.id),
          price: rate.perUsePrice.priceLabel || `${rate.perUsePrice.price} ${data.currency}`,
          description: t(`billing.usage.${rate.id}.description`, { defaultValue: rate.description || `${getServiceName(rate.id)} messaging service` }),
          icon: getServiceIcon(rate.id),
          hasCountrySelect: false,
        });
      } else if (rate.billingStrategy === CreditBillingStrategy.PER_COUNTRY && rate.perCountryPrices) {
        // For SMS - show with country selector
        const availableCountries = Object.keys(rate.perCountryPrices);
        if (availableCountries.length > 0) {
          const firstAvailableCountry = availableCountries[0];
          const selectedCountry = selectedCountries[rate.id] || firstAvailableCountry;
          const selectedRate = rate.perCountryPrices[selectedCountry];
          
          const ratePerCountry = Object.entries(rate.perCountryPrices).map(([countryCode, countryPrice]) => ({
            rate: countryPrice.price,
            currency: data.currency,
            countryCode,
          }));

          items.push({
            type: rate.id,
            name: getServiceName(rate.id),
            price: selectedRate ? `${selectedRate.price} ${data.currency}` : "",
            description: t("billing.usage.sms.description", { defaultValue: rate.description || "SMS messaging service" }),
            icon: getServiceIcon(rate.id),
            availableCountries,
            selectedCountry,
            hasCountrySelect: true,
            ratePerCountry,
          });
        }
      }
    });

    return items;
  }, [data.currency, selectedCountries, t, validUsageRates]);

  const usageRateItems = useMemo(
    () => prepareUsageRateItems(),
    [prepareUsageRateItems]
  );

  // Handle country selection change
  const handleCountryChange = (serviceType: string, countryCode: string) => {
    setSelectedCountries(prev => ({
      ...prev,
      [serviceType]: countryCode,
    }));
  };

  // Get current price for selected country
  const getCurrentPrice = (item: UsageRateItem): string => {
    if (!item.hasCountrySelect || !item.ratePerCountry || !item.selectedCountry) {
      return item.price;
    }
    const selectedRate = item.ratePerCountry.find(
      (rate) => rate.countryCode === item.selectedCountry
    );
    return selectedRate
      ? `${selectedRate.rate} ${selectedRate.currency}`
      : item.price;
  };

  return (
    <>
      <Card>
        <BlockStack gap="400">
          <InlineStack align="space-between">
            <Text variant="headingMd" as="h2">
              {t("settings.subscription.credits.title", { defaultValue: "Credits usage" })}
            </Text>
            <Button
              variant="plain"
              icon={ExportIcon}
              onClick={handleExportCredits}
              loading={isExporting}
            >
              {t("settings.subscription.credits.export", { defaultValue: "Export" })}
            </Button>
          </InlineStack>

          <Text variant="bodyMd" as="p" tone="subdued">
            {credits.description}
          </Text>

          {/* Current Balance */}
          <Box padding="400" background="bg-surface-secondary" borderRadius="200">
            <BlockStack gap="200">
              <Text variant="bodyMd" as="p" tone="subdued">
                {t("settings.subscription.credits.currentBalance", { defaultValue: "Current Balance" })}
              </Text>
              <Text variant="headingLg" as="p">
                {credits.currentLabel || 
                 (credits.current !== undefined 
                   ? `${credits.current} ${data.currency}`
                   : t("settings.subscription.credits.noBalance", { defaultValue: "No balance available" })
                 )}
              </Text>
            </BlockStack>
          </Box>

          <Box>
            <Button
              variant="primary"
              size="large"
              onClick={() => setIsModalOpen(true)}
            >
              {t("settings.subscription.credits.addCredits", { defaultValue: "Add credits" })}
            </Button>
          </Box>

          {/* Usage Rates */}
          <BlockStack gap="200">
            <Text variant="headingXs" as="h3">
              {t("settings.subscription.credits.usageRates", { defaultValue: "Usage Rates" })}
            </Text>
            <Text variant="bodyMd" as="p" tone="subdued">
              {t("settings.subscription.credits.usageRatesDescription", {
                defaultValue: "Credits will be automatically deducted based on your messaging usage."
              })}
            </Text>
            
            {/* Usage Rate Items List */}
            <Card padding="0">
              <BlockStack gap="0">
                {usageRateItems.map((item, index) => (
                  <Box key={item.type}>
                    {index > 0 && (
                      <Bleed marginInline="400">
                        <Divider />
                      </Bleed>
                    )}
                    <Box padding="400">
                      <InlineStack gap="400" align="space-between" blockAlign="center">
                        <InlineStack gap="400" blockAlign="center">
                          {/* Service Icon */}
                          <Thumbnail
                            source={item.icon}
                            alt={`${item.name} icon`}
                            size="small"
                          />
                          
                          {/* Service Info */}
                          <BlockStack gap="100">
                            <Text variant="bodyMd" as="p" fontWeight="medium">
                              {item.name}
                            </Text>
                            <Text variant="bodySm" as="p" tone="subdued">
                              {item.description}
                            </Text>
                          </BlockStack>
                        </InlineStack>
                        
                        {/* Price and Country Selector */}
                        <InlineStack gap="300" blockAlign="center">
                          {item.hasCountrySelect && item.availableCountries ? (
                            <>
                              <Text variant="bodyMd" as="p" fontWeight="medium">
                                {getCurrentPrice(item)}
                              </Text>
                                <CountryCombobox
                                  value={item.selectedCountry || ""}
                                  onChange={(value) => handleCountryChange(item.type, value)}
                                  countryISO3Codes={item.availableCountries}
                                  labelHidden={true}
                                />
                            </>
                          ) : (
                            <Text variant="bodyMd" as="p" fontWeight="medium">
                              {item.price}
                            </Text>
                          )}
                        </InlineStack>
                      </InlineStack>
                    </Box>
                  </Box>
                ))}
              </BlockStack>
            </Card>
          </BlockStack>
        </BlockStack>
      </Card>

      <AddCreditsModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        minPurchaseAmount={credits.minAmount || 5}
        maxPurchaseAmount={credits.maxAmount || 10000}
        currency={data.currency}
        clinicId={clinicId}
      />
    </>
  );
} 
